import { useFetchPageV2 } from "~/core/composables/useFetchPageV2";
import { useSearch } from "~/core/composables/useSearch";
import { useSort } from "~/core/composables/useSort";
import type { UseFetchParameters } from "~/core/models/UseFetchParameters.interface";
import filtersUtils from "~/core/utils/filters.utils";
import FleetService from "~/modules/fleet/services/fleet.service";
import type { FiltersSelection } from "~/types";
import { Filters, FiltersType, OPERATOR_RSQL_ILIKE } from "~/types";

export function useFetchFleets({
  organizationCode,
  searchMode,
  useQueryParametersForPagination,
  compatibilityCriteria,
}: UseFetchParameters) {
  const service = FleetService.getOrganizationFleets.bind(FleetService);

  const appliedFilters: FiltersSelection = new Map([
    [
      Filters.FLEET_NAME,
      {
        key: "friendlyName",
        operator: OPERATOR_RSQL_ILIKE,
        value: "",
        valueFormatter: filtersUtils.formatRSQLValue,
      },
    ],
  ]);

  const { searchParams, filters, setFilter, search, onSearch } = useSearch(
    FiltersType.RSQL,
    appliedFilters,
    searchMode,
  );
  const { sort, onSortChange } = useSort();
  const { isLoading, results, error, fetchData } = useFetchPageV2({
    organizationCode,
    service,
    searchParams,
    sort,
    sortOverrideCallback: search,
    useQueryParametersForPagination,
  });

  const localeCompatibilityCriteria = ref(compatibilityCriteria);

  function setCompatibilityCriteria(compatibilityCriteria?: string) {
    localeCompatibilityCriteria.value = compatibilityCriteria;
  }

  async function fetchDataWithCompatibilityCriteria(
    page?: number,
    pageSize?: number,
    params?: Map<string, unknown>,
  ) {
    if (localeCompatibilityCriteria.value) {
      if (!params) {
        params = new Map();
      }
      params.set("compatibilityCriteria", localeCompatibilityCriteria.value);
    }

    return await fetchData(page, pageSize, params);
  }

  return {
    isLoading,
    error,
    results,
    filters,
    fetchData: fetchDataWithCompatibilityCriteria,
    setCompatibilityCriteria,
    setFilter,
    onSortChange,
    search,
    onSearch,
    sort,
  };
}
